import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { FLOW_NODE_CATEGORIES, FLOW_NODE_LIBRARY, GROUP_LABELS } from './flowNodeCatalog';

const SEARCH_ALIASES = {
  api: ['http', 'https', 'request', 'rest', 'webhook', 'endpoint', 'integration'],
  database: ['db', 'data', 'record', 'query', 'storage'],
  function: ['code', 'custom action', 'server action'],
  input: ['user input', 'raw response', 'answer', 'field'],
  question: ['ask', 'answer', 'prompt', 'reply'],
  quick_replies: ['button', 'buttons', 'choice', 'options'],
  message_quick_replies: ['button', 'buttons', 'choice', 'options'],
  store_comment: ['comment', 'note', 'feedback', 'remark'],
  execute_flow: ['subflow', 'jump flow', 'run flow'],
  switch_flow: ['change flow', 'route flow', 'jump flow'],
  chat_handoff: ['live agent', 'human agent', 'transfer'],
  raise_ticket: ['support', 'case', 'helpdesk'],
};

/** Purpose: Normalize punctuation, case, and separators for node search. Why: Alternate spellings should match the same catalog entries. */
const normalizeSearchText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/[^a-z0-9+@.\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const NODE_BADGES = {
  variables: 'Variable',
  database: 'Database',
  api: 'API call',
  function: 'Function',
  send_otp: 'OTP',
  notification_status: 'Notification',
  data_formatter: 'Transform',
  modifier: 'Transform',
  generate_pdf_image: 'Document',
  analytics: 'Analytics',
  send_event: 'Event',
  knowledge_search: 'Knowledge',
  connect_transxt: 'Integration',
  user_event: 'Event',
};

/** Purpose: Choose a product-facing badge for the node type. Why: Shared internal types must not mislabel specialized actions. */
const getNodeBadge = (item) => {
  if (NODE_BADGES[item.key]) return NODE_BADGES[item.key];
  // `api_call` is also a backward-compatible execution primitive for several
  // non-API product nodes. Never leak that internal type into the library.
  if (item.type === 'api_call') return 'Action';
  return String(item.type || 'node').replace(/_/g, ' ');
};

/** Purpose: Combine node and category metadata into searchable text. Why: Authors should find nodes by purpose as well as title. */
const getNodeSearchText = (item) => {
  /** Purpose: Find the node's category metadata. Why: Category descriptions supply useful search terms. */
  const category = FLOW_NODE_CATEGORIES.find((entry) => entry.key === item.category);
  return normalizeSearchText([
    item.key,
    item.label,
    item.description,
    item.category,
    category?.label,
    category?.description,
    item.group,
    GROUP_LABELS[item.group],
    item.type,
    item.config?.label,
    item.config?.prompt,
    item.config?.message,
    item.config?.variable,
    ...(SEARCH_ALIASES[item.key] || []),
  ].filter(Boolean).join(' '));
};

const CHANNEL_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'web', label: 'Web' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'voice', label: 'Voice' },
];

/** Purpose: Identify the channels supported by specialized nodes. Why: The library should expose channel restrictions before selection. */
const getItemChannels = (item) => {
  const key = String(item.key || '').toLowerCase();
  if (key.includes('whatsapp') || ['product_catalogue', 'connect_transxt'].includes(key)) return ['whatsapp'];
  if (key.includes('voice')) return ['voice'];
  if (['carousel_prompt', 'message_carousel'].includes(key)) return ['web', 'whatsapp'];
  return ['web', 'whatsapp', 'voice'];
};

/** Purpose: Choose active-category styling. Why: The selected library group must remain visible. */
const categoryButtonClass = (active) => [
  'group w-full rounded-xl border px-3 py-2.5 text-left transition duration-200',
  active
    ? 'border-slate-900 bg-slate-950 text-white shadow-[0_12px_30px_rgba(15,23,42,0.18)] dark:border-cyan-400/25 dark:bg-cyan-400/10 dark:text-cyan-50'
    : 'border-transparent bg-transparent text-slate-600 hover:border-slate-200 hover:bg-white dark:text-slate-300 dark:hover:border-white/10 dark:hover:bg-white/5',
].join(' ');

/** Purpose: show one node template in the selection drawer. Why: users need a scannable summary before adding a node to the canvas. */
function NodeCard({ item, onSelect }) {
  const Icon = item.icon;
  const channels = getItemChannels(item);
  const isDynamicChat = item.key === 'dynamic_chat';

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={`group relative flex min-h-[108px] w-full items-start gap-3 overflow-hidden rounded-[18px] border p-4 text-left transition duration-200 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 ${isDynamicChat ? 'border-violet-300 bg-gradient-to-br from-violet-50 via-white to-cyan-50 shadow-[0_12px_30px_rgba(124,58,237,0.12)] hover:border-violet-400 hover:shadow-[0_18px_36px_rgba(124,58,237,0.18)] dark:border-violet-400/30 dark:from-violet-500/15 dark:via-slate-950 dark:to-cyan-500/10' : 'border-slate-200/90 bg-white/95 shadow-[0_6px_20px_rgba(15,23,42,0.05)] hover:border-cyan-300 hover:shadow-[0_14px_30px_rgba(14,165,233,0.11)] dark:border-white/10 dark:bg-slate-950/75 dark:hover:border-cyan-400/30'}`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-gradient-to-br ${item.accent} text-white shadow-[0_8px_18px_rgba(15,23,42,0.15)] ring-1 ring-white/40`}>
        <Icon sx={{ fontSize: 21 }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="min-w-0 text-[15px] font-bold leading-5 text-slate-900 dark:text-white">{item.label}</div>
          <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
            {getNodeBadge(item)}
          </span>
        </div>
        <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-slate-500 dark:text-slate-400">{item.description}</p>
        <div className="mt-2 flex flex-wrap gap-1">
          {channels.map((channel) => <span key={channel} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-slate-500 dark:bg-white/5 dark:text-slate-400">{channel}</span>)}
          {isDynamicChat ? <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-violet-700 dark:bg-violet-400/15 dark:text-violet-200">AI</span> : null}
        </div>
      </div>
      <div className="absolute bottom-4 right-4 flex h-7 w-7 translate-x-2 items-center justify-center rounded-full bg-slate-100 text-slate-400 opacity-0 transition group-hover:translate-x-0 group-hover:bg-cyan-500 group-hover:text-white group-hover:opacity-100 dark:bg-white/10">
        <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
      </div>
    </button>
  );
}

/** Purpose: browse, search, and add node templates. Why: a catalog drawer keeps the canvas uncluttered while exposing available flow capabilities. */
export default function NodeSelectionDrawer({
  open,
  search,
  onSearchChange,
  activeCategory,
  onCategoryChange,
  onClose,
  onSelect,
  recentItems = [],
}) {
  const searchInputRef = useRef(null);
  const [channelFilter, setChannelFilter] = useState('all');

  useEffect(() => {
    if (!open) return;
    /** Purpose: Focus search after the drawer opens. Why: Typing should immediately search the node library. */
    const timeout = setTimeout(() => searchInputRef.current?.focus(), 120);
    return () => clearTimeout(timeout);
  }, [open]);

  /** Purpose: Filter nodes by search tokens or active category. Why: Search spans the catalog while empty searches follow navigation. */
  const filteredItems = useMemo(() => {
    const searchTerm = normalizeSearchText(search);
    const searchTokens = searchTerm.split(' ').filter(Boolean);
    return FLOW_NODE_LIBRARY.filter((item) => {
      // Text search is global. Category selection only scopes the library when
      // the search box is empty, so a node can always be found by its name.
      const categoryMatch = searchTerm || activeCategory === 'all' || item.category === activeCategory;
      if (!categoryMatch) return false;
      // Search is intentionally global: selected category/channel only affect
      // browsing. A builder must be able to find any node from any section.
      if (!searchTerm && channelFilter !== 'all' && !getItemChannels(item).includes(channelFilter)) return false;
      if (!searchTerm) return true;
      const searchableText = getNodeSearchText(item);
      return searchTokens.every((token) => searchableText.includes(token));
    });
  }, [activeCategory, search, channelFilter]);

  /** Purpose: Group matching nodes by catalog group. Why: Related node types should be presented together. */
  const groupedItems = useMemo(() => {
    return filteredItems.reduce((accumulator, item) => {
      const group = item.group || 'others';
      if (!accumulator[group]) {
        accumulator[group] = [];
      }
      accumulator[group].push(item);
      return accumulator;
    }, {});
  }, [filteredItems]);
  /** Purpose: Resolve the selected category's metadata. Why: The drawer heading should match its navigation selection. */
  const activeCategoryDetails = FLOW_NODE_CATEGORIES.find((category) => category.key === activeCategory);

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-slate-950/45 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: 36, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 36, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            className="fixed right-4 top-4 z-50 flex h-[calc(100vh-2rem)] w-[min(1480px,calc(100vw-2rem))] overflow-hidden rounded-[30px] border border-white/70 bg-[radial-gradient(circle_at_top_right,rgba(186,230,253,0.38),transparent_34%),linear-gradient(180deg,#f8fbff_0%,#f1f5f9_100%)] shadow-[0_40px_140px_rgba(15,23,42,0.3)] ring-1 ring-slate-900/5 dark:border-white/10 dark:bg-[radial-gradient(circle_at_top_right,rgba(8,145,178,0.14),transparent_35%),linear-gradient(180deg,#020617_0%,#0f172a_100%)]"
          >
            <div className="flex w-[220px] shrink-0 flex-col border-r border-slate-200/80 bg-white/80 p-5 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/70">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500">Node library</div>
                  <div className="mt-1 whitespace-nowrap text-[25px] font-bold tracking-[-0.035em] text-slate-950 dark:text-white">Node Library</div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <CloseRoundedIcon />
                </button>
              </div>

              <div className="space-y-1.5 overflow-y-auto pr-1">
                {FLOW_NODE_CATEGORIES.filter((category) => FLOW_NODE_LIBRARY.some((item) => item.category === category.key)).map((category) => (
                  <button
                    key={category.key}
                    type="button"
                    onClick={() => onCategoryChange(category.key)}
                    className={categoryButtonClass(activeCategory === category.key)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[15px] font-bold">{category.label}</div>
                      <div className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${activeCategory === category.key ? 'bg-white/15 text-white dark:bg-cyan-400/15 dark:text-cyan-100' : 'bg-slate-100 text-slate-500 dark:bg-white/5'}`}>
                        {FLOW_NODE_LIBRARY.filter((item) => item.category === category.key).length}
                      </div>
                    </div>
                    <div className={`mt-1 line-clamp-2 text-[11px] leading-4 ${activeCategory === category.key ? 'text-white/70 dark:text-cyan-100/70' : 'text-slate-500 dark:text-slate-400'}`}>
                      {category.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-col p-5">
              <div className="mb-4 flex items-center gap-3">
                <label className="flex min-w-0 flex-1 items-center gap-3 rounded-[17px] border border-slate-200/90 bg-white/95 px-4 py-3 shadow-[0_8px_24px_rgba(15,23,42,0.05)] transition focus-within:border-cyan-400 focus-within:ring-4 focus-within:ring-cyan-400/10 dark:border-white/10 dark:bg-white/5">
                  <SearchRoundedIcon className="text-slate-400" />
                  <input
                    ref={searchInputRef}
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder="Search for nodes"
                    className="w-full border-0 bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-slate-500"
                  />
                </label>

                <div className="rounded-[18px] border border-slate-200 bg-white/80 px-4 py-3 text-xs font-semibold text-slate-500 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                  Shortcut <kbd className="ml-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-slate-700 dark:border-white/10 dark:bg-white/10 dark:text-white">/</kbd>
                </div>
              </div>

              <div className="mb-4 flex flex-wrap gap-2">
                {CHANNEL_FILTERS.map((channel) => (
                  <button key={channel.key} type="button" onClick={() => setChannelFilter(channel.key)} className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${channelFilter === channel.key ? 'border-slate-900 bg-slate-900 text-white shadow-sm dark:border-cyan-300 dark:bg-cyan-400/15 dark:text-cyan-100' : 'border-slate-200 bg-white/70 text-slate-500 hover:border-cyan-300 hover:text-cyan-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300'}`}>
                    {channel.label}
                  </button>
                ))}
              </div>

              <div className="mb-5 flex items-end justify-between gap-4 border-b border-slate-200/80 pb-4 dark:border-white/10">
                <div>
                  <div className="text-xl font-bold tracking-tight text-slate-950 dark:text-white">{search ? 'Search results' : activeCategoryDetails?.label || 'All nodes'}</div>
                  <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{search ? `Results for “${search}”` : activeCategoryDetails?.description}</div>
                </div>
                <div className="shrink-0 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white dark:bg-cyan-400/15 dark:text-cyan-100">{filteredItems.length} nodes</div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                {recentItems.length > 0 && !search ? (
                  <div className="mb-6">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                      <HistoryRoundedIcon sx={{ fontSize: 16 }} />
                      Recently used
                    </div>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {recentItems.map((item) => (
                        <NodeCard key={`recent-${item.key}`} item={item} onSelect={onSelect} />
                      ))}
                    </div>
                  </div>
                ) : null}

                {Object.keys(groupedItems).length === 0 ? (
                  <div className="flex h-full min-h-[280px] items-center justify-center rounded-[28px] border border-dashed border-slate-300 bg-white/65 text-center text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                    <div>
                      <div className="text-xl font-semibold text-slate-900 dark:text-white">No matching nodes</div>
                      <div className="mt-2 text-sm">Try the node name, purpose, type, or a related keyword.</div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(groupedItems).map(([groupKey, items]) => (
                      <section key={groupKey}>
                        <div className="mb-3 text-sm font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                          {GROUP_LABELS[groupKey] || 'Nodes'}
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                          {items.map((item) => (
                            <NodeCard key={item.key} item={item} onSelect={onSelect} />
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
